/* One program page from data: /programs/<slug>
   ----------------------------------------------------------------------
   The owner wants every program to have its own page, "just like"
   Japanese Mental Arithmetic (2026-10-07). This template uses the same
   design (ProgramDetail.css, `prog-rich`): photo hero, trust strip,
   numbered sections, closing call to action. Records live in
   src/data/programPages.js; facts there come from the For Schools build
   prompt only, and the owner's answers are added as they arrive.

   Sections: hero → trust strip → The programme → The levels (only a
   record with `levels`: Coding's Builders and Studio, with Aivy) → How
   it builds (navy) → What is included / what your child leaves with
   (and who it is for, once the owner says) → Where it runs → the other
   four programs → call to action. Section numbers count up on their
   own, so a page without levels still reads I to IV. */

import { srcSetFor, SIZES } from "../../lib/img";
import React, { useEffect, useRef } from "react";
import { useLocale, useL } from "../../i18n/LocaleContext";
import SEO from "../../components/SEO";
import SiteHeader from "../../components/SiteHeader";
import LocaleLink from "../../components/LocaleLink";
import { PROGRAM_PAGES, PROGRAM_PAGE_UI } from "../../data/programPages";
import { PROGRAMS } from "../../data/offer";
import { programCourseSchema, faqPageSchema, breadcrumbSchema } from "../../data/schemas";
import ProgramFaq from "./ProgramFaq";
import { ARROW } from "../../components/catalog/CatalogParts";
import { track } from "../../lib/analytics";
import "../Home.css";
import "../Programs.css";
import "./ProgramDetail.css";

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ICON_CLOCK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15.5 14" />
  </svg>
);
/* One icon per step card and per trust item, in order. */
const ICON_STEP = [
  <svg key="a" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>,
  <svg key="b" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  <svg key="c" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>,
  <svg key="d" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 21h8" /><path d="M12 17v4" /><path d="M6 4h12v5a6 6 0 0 1-12 0V4z" /><path d="M6 6H3v2a3 3 0 0 0 3 3" /><path d="M18 6h3v2a3 3 0 0 1-3 3" /></svg>,
];

export default function ProgramPage({ slug }) {
  const { lp, locale } = useLocale();
  const raw = PROGRAM_PAGES[slug];
  const p = useL(raw);
  const ui = useL(PROGRAM_PAGE_UI);
  const others = useL(PROGRAMS.filter((o) => o.id !== slug));
  const rootRef = useRef(null);
  const ROMAN = ["I", "II", "III", "IV", "V", "VI"];
  let n = 0;
  const num = () => ROMAN[n++];

  /* Reveal on scroll, as on the other program pages. */
  useEffect(() => {
    const els = rootRef.current ? Array.from(rootRef.current.querySelectorAll(".reveal")) : [];
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
      }),
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  return (
    <div className="prog-page prog-rich" ref={rootRef}>
      <SEO
        title={p.seo.title}
        description={p.seo.description}
        path={raw.path}
        image={`${raw.photo.src}.jpg`}
        schema={[
          programCourseSchema(raw, locale),
          ...(raw.faq ? [faqPageSchema(raw.faq.items, locale)] : []),
          breadcrumbSchema([
            { name: PROGRAM_PAGE_UI.home, path: "/" },
            { name: PROGRAM_PAGE_UI.programs, path: "/programs" },
            { name: raw.name, path: raw.path },
          ], locale),
        ]}
      />
      <SiteHeader />
      <main id="main">

      {/* ── Hero ── */}
      <header className="el-hero">
        <div className="el-hero-left">
          <span className="el-hero-tag">{p.tag}</span>
          <h1 className="el-hero-title el-display">
            {p.title.before}<em>{p.title.em}</em>{p.title.after}
          </h1>
          <p className="el-hero-headline">{p.headline}</p>
          <p className="el-hero-body">{p.body}</p>
          <div className="el-not-badges">
            {p.badges.map((b, i) => <span key={i} className="el-not-badge">{b}</span>)}
          </div>
          <div className="el-hero-actions">
            <a href={lp("/#contact")} className="btn btn-gold-lg" onClick={() => track("program_register_click", { program: slug })}>
              {ui.register}
            </a>
            <LocaleLink to="/schools#future-skills" className="el-text-link" onClick={() => track("program_schools_click", { program: slug })}>
              <span>{ui.forSchools}</span>{ARROW}
            </LocaleLink>
          </div>
        </div>
        <div className="el-hero-right">
          <picture>
            <source srcSet={srcSetFor(p.photo.src)} sizes={SIZES.half} type="image/webp" />
            <img src={`${p.photo.src}.jpg`} alt={p.photo.alt} className="el-hero-photo" style={raw.photoPos ? { objectPosition: raw.photoPos } : undefined} />
          </picture>
          <div className="el-hero-photo-wash" />
          <div className="el-hero-bracket" />
        </div>
      </header>

      {/* Trust strip */}
      <div className="el-trust">
        {p.trust.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="el-trust-sep" />}
            <div className="el-trust-item">{ICON_STEP[i % ICON_STEP.length]} {item}</div>
          </React.Fragment>
        ))}
      </div>

      <div className="prog-content">

        {/* ═══════════ I · THE PROGRAMME ═══════════ */}
        <section className="el-section">
          <div className="el-inner">
            <span className="el-label reveal">{num()} &nbsp;·&nbsp; {ui.labelProgram}</span>
            <h2 className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>
              {p.intro.title[0]}<br />{p.intro.title[1]}
            </h2>
            <p className="el-lead reveal" style={{ "--d": "120ms" }}>{p.intro.lead}</p>
            <div className="el-subblock el-subblock--flush reveal" style={{ "--d": "180ms" }}>
              <div className="el-session-spec">
                {ICON_CLOCK} {p.intro.spec[0]} <i />{" "}<span>{p.intro.spec[1]}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ THE LEVELS (Coding: Builders and Studio) ═══════════ */}
        {p.levels && (
          <section className="el-section" style={{ paddingTop: 0 }} aria-labelledby="levels-title">
            <div className="el-inner">
              <span className="el-label reveal">{num()} &nbsp;·&nbsp; {ui.labelLevels}</span>
              <h2 id="levels-title" className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>
                {p.levels.title[0]}<br />{p.levels.title[1]}
              </h2>
              <p className="el-lead reveal" style={{ "--d": "120ms" }}>{p.levels.lead}</p>
              <div className="el-levels">
                {p.levels.items.map((l, i) => (
                  <article key={l.key} id={`level-${l.key}`} className="el-level reveal" style={{ "--d": `${i * 120}ms` }}>
                    <p className="el-level-grades">{l.grades}</p>
                    <h3 className="el-level-name">{l.name}</h3>
                    <p className="el-level-tagline">{l.tagline}</p>
                    <p className="el-level-intro">{l.intro}</p>
                    <p className="el-level-ratio">{l.ratio}</p>
                    <p className="el-subhead">{p.levels.learnTitle}</p>
                    <ul className="el-level-list">
                      {l.learn.map((x, j) => (
                        <li key={j}><span className="el-who-mark">{CHECK}</span><span>{x}</span></li>
                      ))}
                    </ul>
                    <p className="el-subhead">{p.levels.makeTitle}</p>
                    <ul className="el-level-list el-level-list--make">
                      {l.make.map((x, j) => (
                        <li key={j}><span className="el-who-mark">{CHECK}</span><span>{x}</span></li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ═══════════ HOW IT BUILDS (NAVY) ═══════════ */}
        <section className="el-section el-plan">
          <div className="el-inner">
            <span className="el-label el-label--light reveal">{num()} &nbsp;·&nbsp; {ui.labelBuild}</span>
            <h2 className="el-h2 el-h2--light el-display reveal" style={{ "--d": "60ms" }}>
              {p.build.title[0]}<br />{p.build.title[1]}
            </h2>
            <p className="el-lead el-lead--light reveal" style={{ "--d": "120ms" }}>{p.build.lead}</p>
            <div className="el-session-cards">
              {p.build.steps.map((s, i) => (
                <div key={i} className="el-scard reveal" style={{ "--d": `${i * 90}ms` }}>
                  <div className="el-scard-icon">{ICON_STEP[i % ICON_STEP.length]}</div>
                  <p className="el-scard-title">{s.title}</p>
                  <p className="el-scard-desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════ WHAT IS INCLUDED ═══════════ */}
        <section className="el-section el-who">
          <div className="el-inner">
            <span className="el-label reveal">{num()} &nbsp;·&nbsp; {ui.labelIncluded}</span>
            <div className="el-twocol">
              <div className="el-col reveal">
                <p className="el-subhead">{p.who ? ui.whoTitle : ui.includedTitle}</p>
                <div className="el-who-list el-who-list--single">
                  {(p.who || p.included).map((w, i) => (
                    <div key={i} className="el-who-item"><span className="el-who-mark">{CHECK}</span>{w}</div>
                  ))}
                </div>
              </div>
              <div className="el-col reveal" style={{ "--d": "120ms" }}>
                <p className="el-subhead">{ui.leaveTitle}</p>
                <div className="el-benefits el-benefits--single">
                  {p.leave.map((o, i) => (
                    <div key={i} className="el-benefit">
                      <span className="el-benefit-check">{CHECK}</span>
                      <p className="el-benefit-text">{o}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ WHERE IT RUNS ═══════════ */}
        <section className="el-section" style={{ paddingTop: 0 }}>
          <div className="el-inner">
            <span className="el-label reveal">{num()} &nbsp;·&nbsp; {ui.labelWhere}</span>
            <div className={`el-session-cards el-session-cards--${p.where.length === 2 ? "two" : "three"}`}>
              {p.where.map((w, i) => (
                <div key={w.key} className="el-scard reveal" style={{ "--d": `${i * 90}ms` }}>
                  <p className="el-scard-title">{w.title}</p>
                  <p className="el-scard-desc">{w.desc}</p>
                </div>
              ))}
            </div>
            {p.note && <p className="el-lead el-program-note reveal">{p.note}</p>}
            <p className="el-lead el-program-note reveal">
              <LocaleLink to="/schools#future-skills" className="el-program-link">{ui.wherePrices} {ARROW}</LocaleLink>
              <br />{ui.familiesLine}
            </p>
          </div>
        </section>

        {/* ═══════════ QUESTIONS PARENTS ASK ═══════════ */}
        {p.faq && (
          <section className="el-section" style={{ paddingTop: 0 }} aria-labelledby="faq-title">
            <div className="el-inner">
              <span className="el-label reveal">{num()} &nbsp;·&nbsp; {p.faq.label}</span>
              <h2 id="faq-title" className="visually-hidden">{p.faq.label}</h2>
              <ProgramFaq items={p.faq.items} />
            </div>
          </section>
        )}

        {/* ═══════════ THE OTHER PROGRAMS ═══════════ */}
        <section className="el-section" style={{ paddingTop: 0 }}>
          <div className="el-inner">
            <span className="el-label reveal">{ui.labelOther}</span>
            <ul className="el-program-others reveal">
              {others.map((o) => (
                <li key={o.id}>
                  <LocaleLink to={o.page} className="el-program-other" onClick={() => track("program_other_click", { from: slug, to: o.id })}>
                    <span className="el-program-other-eyebrow">{o.eyebrow}</span>
                    <span className="el-program-other-name">{o.name}</span>
                    {ARROW}
                  </LocaleLink>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      {/* ── Closing call to action ── */}
      <section className="el-cta">
        <div className="el-cta-inner">
          <h2 className="el-cta-title el-display">{ui.ctaTitle}</h2>
          <p className="el-cta-sub">{ui.ctaSub}</p>
          <div className="el-cta-actions">
            <a href={lp("/#contact")} className="btn btn-gold-lg">{ui.register}</a>
            <LocaleLink to="/schools#enquire" className="btn btn-outline-white">{ui.schoolsCta}</LocaleLink>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}

/* One route component per program, for the router in src/App.js. */
export function programRoute(slug) {
  function ProgramRoute() {
    return <ProgramPage slug={slug} />;
  }
  ProgramRoute.displayName = `ProgramPage(${slug})`;
  return ProgramRoute;
}
